import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {ConfiguratorDatastoreService, ConfiguratorDataViewService, DatastoreDto, DataViewDto, DataViewLiteDto} from '@tj/tj-dmp-lib-api';
import { FilterMenuByPermissionsPipe } from '../../pipes/filter-menu-by-permissions.pipe';
import {AccountService} from '../../auth/account.service';

@Component({
    selector: 'dmp-view-editor',
    templateUrl: './view-editor.component.html',
    styleUrls: ['./view-editor.component.scss'],
    standalone: false
})
export class ViewEditorComponent implements OnInit {

    breadcrumb = [
        {label: this.translate.instant('menu.data-center.label')},
        {label: this.translate.instant('menu.data-center.views')}
    ];
    description = this.translate.instant('viewEditor.list.description');

    isSuperAdmin = false;
    data: DataViewLiteDto[];
    datastore: DatastoreDto;
    selectedViews: DataViewLiteDto[] = [];
    uploadingFile = false;
    exportMenuItems: MenuItem[] = [];
    private actionItemsByViewId: Record<string, MenuItem[]> = {};

    @ViewChild('viewFileInput') viewFileInput: ElementRef;

    get hasActions(): boolean {
        const sample = this.data?.[0];
        if (!sample) {
            return false;
        }
        return this.filterMenuByPermissionsPipe.transform(this.getItemsListAction(sample)).length > 0;
    }
    constructor(private router: Router,
                private confirmationService: ConfirmationService,
                private translate: TranslateService,
                private messageService: MessageService,
                private configuratorDataViewService: ConfiguratorDataViewService,
                private filterMenuByPermissionsPipe: FilterMenuByPermissionsPipe,
                private accountService: AccountService,
                private datastoreService: ConfiguratorDatastoreService) {
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.data-center.label')},
            {label: this.translate.instant('menu.data-center.views')}
        ];
        this.description = this.translate.instant('viewEditor.list.description');
        this.initExportMenu();
    }

    ngOnInit(): void {
        this.initExportMenu();
        this.accountService.identity().then(account => {
            if (account) {
                this.isSuperAdmin = account.admin === true;
                if (!this.isSuperAdmin && account.rules?.length > 0) {
                    const savedGroup = localStorage.getItem('group');
                    if (!savedGroup) {
                        const tenantCode = account.rules[0].tenant;
                        this.datastoreService.getAllDatastoresUsingGET().subscribe(datastores => {
                            const found = datastores.find(d => d.code === tenantCode);
                            if (found) {
                                this.datastoreChange(found);
                            }
                        });
                    }
                }
            }
        });
    }

    /**
     * Initialise le menu d'export/import
     */
    private initExportMenu() {
        this.exportMenuItems = [
            {
                label: this.translate.instant('subscriptions.importFile'),
                icon: 'fas fa-upload',
                command: () => this.triggerFileUpload()
            },
            {
                label: this.translate.instant('subscriptions.exportSelection'),
                icon: 'fas fa-file-export',
                command: () => this.exportSelectedViews(),
                disabled: !this.selectedViews || this.selectedViews.length === 0
            },
            {
                label: this.translate.instant('subscriptions.exportAll'),
                icon: 'fas fa-download',
                command: () => this.exportAllViews(),
                disabled: false
            }
        ];
    }

    /**
     * Déclenche l'ouverture du sélecteur de fichier
     */
    triggerFileUpload() {
        this.viewFileInput.nativeElement.click();
    }

    /**
     * Gère l'import de fichiers (JSON ou ZIP)
     */
    importFile(event: any) {
        const files: FileList = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        this.uploadingFile = true;
        const file: File = files[0];

        if (file.name.endsWith('.json')) {
            this.handleJsonImport(file);
        } else if (file.name.endsWith('.zip')) {
            this.handleZipImport(file);
        } else {
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('datastore.errorSummary'),
                detail: this.translate.instant('subscriptions.unsupportedFormatDetail')
            });
            this.uploadingFile = false;
        }

        // Reset input
        event.target.value = '';
    }

    /**
     * Importe un fichier JSON
     */
    private handleJsonImport(file: File) {
        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
            const jsonContent: string = e.target.result;
            this.configuratorDataViewService.importDataViewJson(this.datastore.code, jsonContent).subscribe(
                (view: DataViewDto) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('viewEditor.list.importSuccessDetail', {name: view.name})
                    });
                    this.updateListTable();
                },
                (error) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                    });
                }
            );
        };
        reader.readAsText(file);
    }

    /**
     * Importe un fichier ZIP
     */
    private handleZipImport(file: File) {
        this.configuratorDataViewService.importDataViewsZip(this.datastore.code, file).subscribe(
            (views: DataViewDto[]) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('viewEditor.list.importSuccessMultipleDetail', {count: views.length})
                });
                this.updateListTable();
            },
            (error) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte une seule vue en JSON
     */
    exportViewJson(view: DataViewLiteDto) {
        this.configuratorDataViewService.exportDataViewJson(this.datastore.code, view.id).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `view-${view.code}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('viewEditor.list.exportSuccessDetail')
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte plusieurs vues en ZIP
     */
    exportSelectedViews() {
        if (!this.selectedViews || this.selectedViews.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('viewEditor.list.noViewSelectedDetail')
            });
            return;
        }

        const ids = this.selectedViews.map(v => v.id);
        this.configuratorDataViewService.exportDataViewsZip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `views-export.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('viewEditor.list.exportSuccessMultipleDetail', {count: this.selectedViews.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte toutes les vues en ZIP
     */
    exportAllViews() {
        if (!this.data || this.data.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('viewEditor.list.noViewToExportDetail')
            });
            return;
        }

        const ids = this.data.map(v => v.id);
        this.configuratorDataViewService.exportDataViewsZip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `views-export-all.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('viewEditor.list.exportSuccessMultipleDetail', {count: this.data.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Met à jour l'état des items du menu export
     */
    private updateExportMenuState() {
        if (this.exportMenuItems && this.exportMenuItems.length > 0) {
            // Update "Exporter la sélection" (index 1)
            if (this.exportMenuItems[1]) {
                this.exportMenuItems[1].disabled = !this.selectedViews || this.selectedViews.length === 0;
            }
            // Update "Exporter tout" (index 2)
            if (this.exportMenuItems[2]) {
                this.exportMenuItems[2].disabled = !this.data || this.data.length === 0;
            }
        }
    }

    onSelectionChange() {
        this.updateExportMenuState();
    }

    datastoreChange(datastore: DatastoreDto) {
        this.datastore = datastore;
        this.updateListTable();
    }

    private updateListTable() {
        if (this.datastore) {
            this.configuratorDataViewService.getAllDataViewsUsingGET(this.datastore.code)
                .subscribe((res) => {
                    this.data = res;
                    this.actionItemsByViewId = {};
                    this.updateExportMenuState();
                });
        } else {
            this.data = [];
            this.actionItemsByViewId = {};
        }
    }

    getItemsListAction(view: DataViewLiteDto): MenuItem[] {
        const cached = this.actionItemsByViewId[view.id];
        if (cached) {
            return cached;
        }

        const items: MenuItem[] = [
            {
                label: this.translate.instant('subscriptions.edit'),
                icon: 'fas fa-pen',
                permission: 'DATAVIEWSETTINGS.edit',
                command: () => {
                    this.router.navigate(['/h/settings/view-editor/', this.datastore.code, view.id]);
                }
            },
            {
                label: this.translate.instant('viewEditor.list.consultData'),
                icon: 'fas fa-database',
                permission: 'DATAVIEWS.read',
                command: () => {
                    this.router.navigate(['/h/view', view.code.split('-')[0]],
                        { queryParams: { group: this.datastore.code, view: view.code } });
                }
            },
            {
                separator: true
            },
            {
                label: this.translate.instant('viewEditor.list.downloadJson'),
                icon: 'fas fa-download',
                permission: 'DATAVIEWSETTINGS.read',
                command: () => {
                    this.exportViewJson(view);
                }
            }
        ];

        this.actionItemsByViewId[view.id] = items;
        return items;
    }

    private delete(name: string, event) {

    }
}
